/**
 * Decode a base64 string to a UTF-8 string.
 *
 * Unlike `atob()`, which treats each byte as a Latin-1 character, this
 * function correctly decodes multi-byte UTF-8 sequences (e.g. French
 * accented characters like é, è, ç).
 */
export function base64ToUtf8(base64: string): string {
  const binaryStr = atob(base64);
  const bytes = Uint8Array.from(binaryStr, (c) => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

// fixes "RangeError: Maximum call stack size exceeded when trying to pass"
// more than 2^16 (65536)
// Safari has the lowest limit, however all browsers will error at a certain size
// ie: String.fromCharCode(...new Uint8Array(response.data))
export function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Sanitize ZPL for Labelary preview rendering.
 *
 * Handles:
 * - Line ending normalization and ^POI removal
 * - Tilde (~) characters in field data that Labelary misinterprets as ZPL
 *   control commands (e.g. ~JA = "Cancel All"), causing blank labels.
 *   Carriers like UPS escape tildes with ~CT sequences; we resolve those
 *   and any remaining raw tildes into ^FH hex notation (_7E).
 */
export function sanitizeZplForLabelary(zpl: string): string {
  let result = zpl.replace(/(\r\n|\n|\r|\^POI)/gm, '');

  // Resolve UPS-style ~CT<c>~<c>CT~ escape sequences (literal tilde)
  // into a temporary placeholder before field-level processing
  result = result.replace(/~CT.~.CT~/g, '\uE000TILDE\uE000');

  // Escape tildes inside ^FD/^FV field data blocks, adding ^FH for hex mode.
  // Note: requires well-formed ZPL with ^FS terminators; malformed fields without ^FS are not escaped.
  result = result.replace(/(\^FH)?(\^F[DV])([\s\S]*?)(\^FS)/g, (_match, existingFH, prefix, content, suffix) => {
    if (!/\uE000TILDE\uE000|~/.test(content)) return `${existingFH ?? ''}${prefix}${content}${suffix}`;

    const escaped = content
      .replace(/_/g, '_5F') // escape literal underscores FIRST (0x5F = '_')
      .replace(/\uE000TILDE\uE000/g, '_7E')
      .replace(/~/g, '_7E');
    return `^FH${prefix}${escaped}${suffix}`;
  });

  // Clean up any remaining placeholders outside field data (shouldn't happen, but safe)
  result = result.replace(/\uE000TILDE\uE000/g, '~');

  return result;
}
